import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Empty, Input, Message, Modal, Select, Space, Table, Tag, Typography } from "@arco-design/web-react";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import moment from "moment";

import Content from "../Layout/Content";
import SummaryCard, { SummaryCardGroup } from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, PostApi, SaveApi, DeleteApi } from "../Api/AuthenticatedApi";
import { DOMAIN_STATUSES, canDelete, deliveryWarning, poolSummary, saturation, setupState, statusColor, statusLabel } from "./domainPool";
import SpyDomainSetup from "./SpyDomainSetup";

const { Text, Paragraph } = Typography;

// The pool of domains seed addresses are minted under. Platform-level: the API
// gates every route on the admin allowlist, and this screen is only reachable
// when /api/me says so.
export default function SpyDomains() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Rows showing their setup. Domains still being set up open by themselves.
  const [expanded, setExpanded] = useState(null);
  const [checkingId, setCheckingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    FetchApi(getAccessTokenSilently, "/api/admin/spy-domains/",
      (data) => {
        setLoading(false);
        if (Array.isArray(data)) {
          setDomains(data);
          setForbidden(false);
          setExpanded((prev) => prev || data.filter((d) => !d.ready_date).map((d) => d.id));
        }
        else { setForbidden(true); }
      },
      () => navigate("/login"),
      () => { setLoading(false); setForbidden(true); });
  }, [getAccessTokenSilently, navigate]);

  useEffect(load, [load]);

  const add = () => {
    if (!newDomain.trim()) return;
    setSaving(true);
    PostApi(getAccessTokenSilently, "/api/admin/spy-domains/",
      (created) => {
        setSaving(false); setAddOpen(false); setNewDomain(""); setNewNotes("");
        Message.success("Added. Follow the steps to finish setting it up.");
        if (created && created.id) setExpanded((prev) => [...(prev || []), created.id]);
        load();
      },
      () => navigate("/login"),
      (err) => { setSaving(false); Message.error((err && err.message) || "Failed to add the domain"); },
      { domain: newDomain.trim(), notes: newNotes.trim() });
  };

  const setStatus = (domain, status) => {
    SaveApi(getAccessTokenSilently, `/api/admin/spy-domains/${domain.id}`,
      () => { Message.success(`${domain.domain} is now ${statusLabel(status).toLowerCase()}`); load(); },
      () => navigate("/login"),
      (err) => Message.error((err && err.message) || "Failed to change the status"),
      { status, notes: domain.notes || "" });
  };

  const check = (domain) => {
    setCheckingId(domain.id);
    PostApi(getAccessTokenSilently, `/api/admin/spy-domains/${domain.id}/check`,
      (updated) => {
        setCheckingId(null);
        setDomains((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)));
        if (updated.ready_date && !domain.ready_date) Message.success(`${updated.domain} is ready and taking new seeds`);
      },
      () => navigate("/login"),
      (err) => { setCheckingId(null); Message.error((err && err.message) || "Failed to check the domain"); });
  };

  const remove = (domain) => {
    Modal.confirm({
      title: "Remove from the pool",
      content: `Remove ${domain.domain}? Nothing is planted on it, so no seeds are affected. Its DNS records, and its place in Amazon's receipt rule, stay: remove them by hand if you're done with the domain.`,
      okButtonProps: { status: "danger" },
      onOk: () => new Promise((resolve) => {
        DeleteApi(getAccessTokenSilently, `/api/admin/spy-domains/${domain.id}`,
          () => { Message.success("Removed"); load(); resolve(); },
          () => { navigate("/login"); resolve(); },
          (err) => { Message.error((err && err.message) || "Failed to remove"); resolve(); });
      }),
    });
  };

  if (forbidden) {
    return (
      <Content>
        <SummaryCard title="Not available">
          <Text type="secondary">This console isn't available on your account.</Text>
        </SummaryCard>
      </Content>
    );
  }

  const summary = poolSummary(domains);

  const columns = [
    {
      title: "Domain",
      dataIndex: "domain",
      render: (value, row) => (
        <div>
          <Text code>{value}</Text>
          {row.notes && <div style={{ fontSize: 12, color: "var(--color-text-3)" }}>{row.notes}</div>}
        </div>
      ),
    },
    {
      title: "Setup",
      dataIndex: "ready_date",
      width: 170,
      render: (_, row) => {
        const state = setupState(row);
        return <Tag color={state.color}>{state.label}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 150,
      render: (status, row) => (
        <Select
          size="mini"
          value={status}
          style={{ width: 120 }}
          onChange={(next) => setStatus(row, next)}
        >
          {DOMAIN_STATUSES.map((option) => (
            <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Customers",
      dataIndex: "organization_count",
      width: 120,
      render: (count, row) => {
        const { level } = saturation(row);
        return (
          <Tag color={level === "high" ? "red" : level === "watch" ? "orange" : statusColor(row.status)}>
            {count || 0}
          </Tag>
        );
      },
    },
    { title: "Subscriptions", dataIndex: "address_count", width: 130, render: (n) => n || 0 },
    { title: "Emails in", dataIndex: "message_count", width: 120, render: (n) => n || 0 },
    {
      title: "Last mail",
      dataIndex: "last_message_date",
      width: 140,
      render: (d) => (d ? <span title={moment(d).format("MMM D, YYYY h:mm A")}>{moment(d).fromNow()}</span> : <Text type="secondary">never</Text>),
    },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, row) =>
        canDelete(row) ? (
          <Button type="text" icon={<IconDelete />} onClick={() => remove(row)} aria-label="Remove" />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }} title="Seeds are planted here — drain it instead">in use</Text>
        ),
    },
  ];

  const advice = domains.map((d) => ({ domain: d, ...saturation(d) })).filter((a) => a.level !== "none");
  const delivery = domains.map((d) => ({ domain: d, warning: deliveryWarning(d) })).filter((a) => a.warning);

  return (
    <Content>
      <SimpleBreadcrumbs elements={[{ name: "Admin" }, { name: "Spy Domains" }]} />

      <SummaryCardGroup>
        <SummaryCard title="Domains" value={summary.total} />
        <SummaryCard title="Taking new seeds" value={summary.active} />
        <SummaryCard title="Subscriptions" value={summary.addresses} />
        <SummaryCard title="Emails collected" value={summary.messages} />
      </SummaryCardGroup>

      <SummaryCard
        title="Seed address domains"
        subTitle="The domains customers' spy addresses are minted under"
      >
        <Paragraph style={{ color: "var(--color-text-3)" }}>
          Addresses are spread across whichever domains are active, filling the least-used
          first, and a watch's two addresses are put on different domains where possible.
          The number to watch is <b>customers</b>: a competitor who recognises one address
          can block the domain, and everyone on it loses their seeds at once. When that
          number gets uncomfortable, add a domain and set the old one to draining — seeds
          already planted keep collecting, and no new ones land there.
        </Paragraph>

        {delivery.map(({ domain, warning }) => (
          <Alert key={domain.id} type="error" style={{ marginBottom: 8 }} title={domain.domain} content={warning} />
        ))}
        {advice.map(({ domain, level, advice: text }) => (
          <Alert
            key={domain.id}
            type={level === "high" ? "warning" : "info"}
            style={{ marginBottom: 8 }}
            title={domain.domain}
            content={text}
          />
        ))}

        <Space style={{ marginBottom: 12, marginTop: 8 }}>
          <Button type="primary" icon={<IconPlus />} onClick={() => setAddOpen(true)}>Add a domain</Button>
          <Button onClick={load}>Refresh</Button>
        </Space>

        <Table
          size="small"
          rowKey="id"
          loading={loading}
          columns={columns}
          data={domains}
          pagination={false}
          noDataElement={<Empty description="No domains in the pool yet" />}
          expandedRowKeys={expanded || []}
          onExpandedRowsChange={(keys) => setExpanded(keys)}
          expandedRowRender={(row) => (
            <SpyDomainSetup domain={row} onCheck={() => check(row)} checking={checkingId === row.id} />
          )}
        />
      </SummaryCard>

      <Modal
        visible={addOpen}
        title="Add a domain"
        okText="Add"
        confirmLoading={saving}
        okButtonProps={{ disabled: !newDomain.trim() }}
        onOk={add}
        onCancel={() => setAddOpen(false)}
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">Domain</Text>
          <Input autoFocus value={newDomain} onChange={setNewDomain} onPressEnter={add} placeholder="inbox.example.com" />
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--color-text-3)" }}>
            Use a domain nothing else gets mail on, that doesn't name Contact Anchor. The app
            sets up Amazon's side, then shows you the two DNS records to add. It takes no
            seeds until every check passes.
          </div>
        </div>
        <div>
          <Text type="secondary">Notes (optional)</Text>
          <Input value={newNotes} onChange={setNewNotes} placeholder="registrar, renewal date, anything worth remembering" />
        </div>
      </Modal>
    </Content>
  );
}
